import React, { useState, useRef, useEffect } from 'react';
import { storage, db } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, updateDoc, getDoc, collection, query, where, getDocs, limit } from 'firebase/firestore';
import './ProfileUpload.css';

const ProfileUpload = ({ user, onClose }) => {
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [username, setUsername] = useState('');
    const [currentUsername, setCurrentUsername] = useState('');
    const [usernameChanged, setUsernameChanged] = useState(false);
    const [validatingUsername, setValidatingUsername] = useState(false);
    const fileInputRef = useRef(null);

    // Fetch existing username and profile image when component mounts
    useEffect(() => {
        if (user?.uid) {
            const fetchUserData = async () => {
                try {
                    const userDoc = await getDoc(doc(db, 'users', user.uid));
                    if (userDoc.exists()) {
                        const userData = userDoc.data();
                        
                        // Check if username exists in the document
                        // First check in userProfile.username (preferred location)
                        if (userData.userProfile?.username) {
                            setUsername(userData.userProfile.username);
                            setCurrentUsername(userData.userProfile.username);
                        } 
                        // Then check in root username (legacy location)
                        else if (userData.username) {
                            setUsername(userData.username);
                            setCurrentUsername(userData.username);
                        }
                        
                        // Check if there's an existing profile image to show
                        // First check in userProfile.profileImage (preferred location)
                        if (userData.userProfile?.profileImage) {
                            setPreview(userData.userProfile.profileImage);
                        } 
                        // Then check in root profileImage (legacy location)
                        else if (userData.profileImage) {
                            setPreview(userData.profileImage);
                        }
                    }
                } catch (error) {
                    console.error('Error fetching user data:', error);
                }
            };
            fetchUserData();
        }
    }, [user]);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        
        if (selectedFile) {
            // Check file type
            if (!selectedFile.type.match('image.*')) {
                setError('Please select an image file');
                return;
            }
            
            // Check file size (limit to 5MB)
            if (selectedFile.size > 5 * 1024 * 1024) {
                setError('File size should be less than 5MB');
                return;
            }
            
            setFile(selectedFile);
            setError('');
            
            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreview(reader.result);
            };
            reader.readAsDataURL(selectedFile);
        }
    };

    const handleUsernameChange = (e) => {
        setUsername(e.target.value);
        setUsernameChanged(true);
    };

    // Check if username is unique
    const isUsernameUnique = async (newUsername) => {
        // If username hasn't changed, no need to check
        if (newUsername === currentUsername) {
            console.log("Username hasn't changed, skipping uniqueness check");
            return true;
        }
        
        try {
            // Skip the check if username is empty
            if (!newUsername.trim()) {
                console.log("Username is empty, skipping uniqueness check");
                return true;
            }
            
            console.log(`Checking if username "${newUsername}" is unique...`);
            
            // Check both locations for username
            const usersRef = collection(db, 'users');
            
            // Try-catch for each query to isolate issues
            let rootMatches = 0;
            try {
                // Check root username field
                const rootQuery = query(usersRef, where('username', '==', newUsername), limit(1));
                const rootSnapshot = await getDocs(rootQuery);
                rootMatches = rootSnapshot.size;
                console.log(`Found ${rootMatches} matches in root username field`);
            } catch (error) {
                console.error("Error checking root username field:", error);
                // Continue with the check, don't fail immediately
            }
            
            let nestedMatches = 0;
            try {
                // Check userProfile.username field
                const nestedQuery = query(usersRef, where('userProfile.username', '==', newUsername), limit(1));
                const nestedSnapshot = await getDocs(nestedQuery);
                nestedMatches = nestedSnapshot.size;
                console.log(`Found ${nestedMatches} matches in userProfile.username field`);
            } catch (error) {
                console.error("Error checking userProfile.username field:", error);
                // Continue with the check, don't fail immediately
            }
            
            // Username is unique if both queries return zero matches
            const isUnique = rootMatches === 0 && nestedMatches === 0;
            console.log(`Username is unique: ${isUnique}`);
            
            return isUnique;
        } catch (error) {
            console.error('Error checking username uniqueness:', error);
            // In case of error, we'll allow the update but log the error
            console.log("Allowing update despite error in uniqueness check");
            return true;
        }
    };

    const handleUpload = async () => {
        if (!file && !usernameChanged) {
            setError('Please select a file or update your username');
            return;
        }
        
        // Validate username if it was changed
        if (usernameChanged && username.trim()) {
            setValidatingUsername(true);
            setError(''); // Clear any previous errors
            
            try {
                const unique = await isUsernameUnique(username.trim());
                
                if (!unique) {
                    setError('Username already taken. Please choose another one.');
                    setValidatingUsername(false);
                    return;
                }
            } catch (error) {
                console.error('Error during username validation:', error);
                setError('Error checking username. Please try again.');
                setValidatingUsername(false);
                return;
            }
            
            setValidatingUsername(false);
        }
        
        try {
            setUploading(true);
            setError('');
            
            // Create a reference to the user document
            const userRef = doc(db, 'users', user.uid);
            
            // Get the current user data to properly update nested fields
            const userDoc = await getDoc(userRef);
            const userData = userDoc.exists() ? userDoc.data() : {};
            
            // Prepare the update data
            let updateData = {};
            
            // Create or update the userProfile object
            updateData['userProfile'] = {
                ...(userData.userProfile || {})
            };
            
            // If a file was selected, upload it and update the profile image
            if (file) {
                // Create a reference to the storage location
                const storageRef = ref(storage, `profile_images/${user.uid}/${Date.now()}_${file.name}`);
                
                // Upload the file
                await uploadBytes(storageRef, file);
                
                // Get the download URL
                const downloadURL = await getDownloadURL(storageRef);
                
                // Add profile image to userProfile object
                updateData['userProfile'].profileImage = downloadURL;
                
                // If there's a legacy profileImage field, remove it
                if (userData.profileImage) {
                    updateData.profileImage = null;
                }
            }
            
            // If username was changed, add it to userProfile object
            if (usernameChanged && username.trim()) {
                updateData['userProfile'].username = username.trim();
                
                // If there's a legacy username field, remove it
                if (userData.username) {
                    updateData.username = null;
                }
            }
            
            // Only update if there's data to update
            if (Object.keys(updateData).length > 0) {
                console.log('Updating user document with:', updateData);
                await updateDoc(userRef, updateData);
                console.log('User document updated successfully');
            }
            
            // Close the upload modal
            onClose();
            
        } catch (error) {
            console.error('Error updating profile:', error);
            setError('Error updating profile. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    const triggerFileInput = () => {
        fileInputRef.current.click();
    };

    return (
        <div className="profile-upload-modal">
            <div className="profile-upload-content">
                {/* Add close button */}
                <button className="close-button" onClick={onClose}>×</button>
                
                <h3>Update Profile</h3>
                
                <div className="new-feature-alert">
                    <p>Hey! Have you seen the new profile functionality? You can now set up and edit your username!</p>
                </div>
                
                <div 
                    className="upload-area"
                    onClick={triggerFileInput}
                >
                    {preview ? (
                        <img 
                            src={preview} 
                            alt="Preview" 
                            className="preview-image"
                        />
                    ) : (
                        <div className="upload-placeholder">
                            <span>Click to select an image</span>
                        </div>
                    )}
                </div>
                
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept="image/*"
                    style={{ display: 'none' }}
                />
                
                {error && <p className="error-message">{error}</p>}
                
                <div className="username-section">
                    <label htmlFor="username">Username:</label>
                    <input
                        type="text"
                        id="username"
                        value={username}
                        onChange={handleUsernameChange}
                        placeholder="Enter your username"
                        className="username-input"
                    />
                    {usernameChanged && username !== currentUsername && (
                        <p className="username-status">
                            {validatingUsername ? 'Checking username availability...' : 'Username will be checked when you update'}
                        </p>
                    )}
                </div>
                
                <div className="upload-actions">
                    <button 
                        onClick={handleUpload}
                        disabled={(!file && !usernameChanged) || uploading || validatingUsername}
                        className="upload-button"
                    >
                        {uploading ? 'Updating...' : validatingUsername ? 'Checking...' : 'Update Profile'}
                    </button>
                    <button 
                        onClick={onClose}
                        className="cancel-button"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProfileUpload;
